import React from 'react';
import {
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
} from '@mui/material';
import {
  Brightness6 as BrightnessIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  SettingsBrightness as SystemModeIcon,
} from '@mui/icons-material';
import { useTheme, ThemeMode } from '../contexts/ThemeContext';

interface ThemeToggleProps {
  showTooltip?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const ThemeToggle: React.FC<ThemeToggleProps> = ({
  showTooltip = true,
  size = 'medium'
}) => {
  const { themeMode, setThemeMode, isDarkMode } = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleThemeSelect = (mode: ThemeMode) => {
    setThemeMode(mode);
    handleClose();
  };

  const getCurrentIcon = () => {
    switch (themeMode) {
      case 'light':
        return <LightModeIcon />;
      case 'dark':
        return <DarkModeIcon />;
      case 'system':
        return <SystemModeIcon />;
      default:
        return <BrightnessIcon />;
    }
  };

  const getThemeLabel = (mode: ThemeMode) => {
    switch (mode) {
      case 'light':
        return 'Világos téma';
      case 'dark':
        return 'Sötét téma';
      case 'system':
        return 'Rendszer beállítás';
      default:
        return 'Téma';
    }
  };

  const getCurrentLabel = () => {
    return getThemeLabel(themeMode);
  };

  const themeButton = (
    <IconButton
      onClick={handleClick}
      size={size}
      aria-label="Téma váltás"
      aria-controls={open ? 'theme-menu' : undefined}
      aria-haspopup="true"
      aria-expanded={open ? 'true' : undefined}
      sx={{
        color: 'inherit',
        transition: 'color 0.3s ease, transform 0.2s ease',
        '&:hover': {
          transform: 'scale(1.1)',
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
        },
      }}
    >
      {getCurrentIcon()}
    </IconButton>
  );

  return (
    <Box>
      {showTooltip ? (
        <Tooltip title={getCurrentLabel()} placement="bottom">
          {themeButton}
        </Tooltip>
      ) : (
        themeButton
      )}

      <Menu
        id="theme-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 180,
          },
        }}
      >
        <MenuItem
          onClick={() => handleThemeSelect('light')}
          selected={themeMode === 'light'}
        >
          <ListItemIcon>
            <LightModeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Világos téma" />
        </MenuItem>

        <MenuItem
          onClick={() => handleThemeSelect('dark')}
          selected={themeMode === 'dark'}
        >
          <ListItemIcon>
            <DarkModeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Sötét téma" />
        </MenuItem>

        <MenuItem
          onClick={() => handleThemeSelect('system')}
          selected={themeMode === 'system'}
        >
          <ListItemIcon>
            <SystemModeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Rendszer beállítás"
            secondary={isDarkMode ? 'Jelenleg: Sötét' : 'Jelenleg: Világos'}
          />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ThemeToggle;